import { NdjsonParseError } from './errors';

export const NDJSON_CONTENT_TYPE = 'application/x-ndjson';
export const DEFAULT_STREAM_BATCH_MS = 100;

const NDJSON_CONTENT_TYPE_PATTERN = /\b(x-)?(ndjson|jsonl|jsonlines)\b/i;
const FLUSH_TIMEOUT = Symbol('flushTimeout');
const BLANK_LINE = Symbol('blankLine');

export const isNdjsonContentType = (contentType = '') =>
  NDJSON_CONTENT_TYPE_PATTERN.test(contentType || '');

// Per the NDJSON spec: accept `\n` and `\r\n`, ignore blank lines, fail on invalid JSON.
const parseLine = (rawLine, lineNumber) => {
  const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
  if (!line.trim()) return BLANK_LINE;

  try {
    return JSON.parse(line);
  } catch (error) {
    throw new NdjsonParseError(lineNumber, line, error);
  }
};

/**
 * Parses a complete NDJSON string into an array of values.
 * @param {string} text
 * @returns {Array<*>}
 */
export const parseNdjson = (text = '') => {
  const items = [];
  `${text ?? ''}`.split('\n').forEach((line, index) => {
    const item = parseLine(line, index + 1);
    if (item !== BLANK_LINE) items.push(item);
  });
  return items;
};

const createFlushTimer = (ms) => {
  let timerId;
  const promise = new Promise((resolve) => {
    timerId = setTimeout(() => resolve(FLUSH_TIMEOUT), ms);
  });
  return { promise, cancel: () => clearTimeout(timerId) };
};

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const toReadableStream = (source) => {
  if (typeof source?.getReader === 'function') return source;
  if (source && 'body' in source) return source.body;
  throw new TypeError('readNdjson expects a fetch Response or a ReadableStream');
};

/**
 * Incrementally reads an NDJSON body and yields arrays ("batches") of parsed values.
 *
 * - Works in every supported browser (uses `getReader()`, not `for await` over the stream).
 * - Handles multi-byte characters and lines split across network chunks.
 * - Batches values so consumers re-render at most once per `batchMs`; a value never waits longer
 *   than `batchMs`, even if the server pauses mid-stream.
 * - Throws `NdjsonParseError` for invalid/truncated lines and propagates network/abort errors, so a
 *   cut-off stream is never mistaken for a complete one.
 * - Cancels the underlying stream when the consumer stops iterating early.
 *
 * @param {Response|ReadableStream<Uint8Array>} source
 * @param {{ batchMs?: number, maxBatchSize?: number }} [options]
 *   batchMs: max time a parsed value is held before being yielded (0 = once per network chunk,
 *   Infinity = only when `maxBatchSize` is reached or the stream ends).
 *   maxBatchSize: max values per yielded batch.
 * @returns {AsyncGenerator<Array<*>>}
 */
export async function* readNdjson(source, options = {}) {
  const { batchMs = DEFAULT_STREAM_BATCH_MS, maxBatchSize = Infinity } = options;
  const stream = toReadableStream(source);

  // Bodiless responses (e.g. 204) or environments without streaming support.
  if (!stream) {
    const items = typeof source?.text === 'function' ? parseNdjson(await source.text()) : [];
    if (items.length) yield items;
    return;
  }

  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let lineNumber = 0;
  let batch = [];
  let batchStartedAt = 0;
  let pendingRead = null;
  let isFinished = false;

  const pushLine = (line) => {
    lineNumber += 1;
    const item = parseLine(line, lineNumber);
    if (item === BLANK_LINE) return;
    if (!batch.length) batchStartedAt = now();
    batch.push(item);
  };

  const pushLines = (isFinal) => {
    let start = 0;
    let newlineIndex;
    while ((newlineIndex = buffer.indexOf('\n', start)) !== -1) {
      pushLine(buffer.slice(start, newlineIndex));
      start = newlineIndex + 1;
    }
    buffer = buffer.slice(start);
    if (isFinal && buffer) {
      pushLine(buffer);
      buffer = '';
    }
  };

  const takeBatch = (size = batch.length) => {
    const taken = batch;
    if (size >= batch.length) {
      batch = [];
      return taken;
    }
    batch = taken.slice(size);
    batchStartedAt = now();
    return taken.slice(0, size);
  };

  try {
    for (;;) {
      if (!pendingRead) pendingRead = reader.read();

      let result;
      if (batch.length && Number.isFinite(batchMs)) {
        const timer = createFlushTimer(Math.max(0, batchMs - (now() - batchStartedAt)));
        try {
          result = await Promise.race([pendingRead, timer.promise]);
        } finally {
          timer.cancel();
        }
        if (result === FLUSH_TIMEOUT) {
          yield takeBatch();
          continue;
        }
      } else {
        result = await pendingRead;
      }
      pendingRead = null;

      if (result.done) {
        isFinished = true;
        buffer += decoder.decode();
        pushLines(true);
        while (batch.length) yield takeBatch(Math.min(batch.length, maxBatchSize));
        return;
      }

      buffer += decoder.decode(result.value, { stream: true });
      pushLines(false);

      while (batch.length >= maxBatchSize) yield takeBatch(maxBatchSize);
      if (batch.length && Number.isFinite(batchMs) && now() - batchStartedAt >= batchMs) {
        yield takeBatch();
      }
    }
  } catch (error) {
    // Hand over the values parsed before the failure, then report it.
    if (batch.length) {
      const parsed = batch;
      batch = [];
      yield parsed;
    }
    throw error;
  } finally {
    if (!isFinished) {
      // An in-flight read may still reject (e.g. on abort) after the consumer has stopped.
      pendingRead?.catch(() => {});
      await reader.cancel().catch(() => {});
    }
    try {
      reader.releaseLock();
    } catch {
      // Already released or the stream errored.
    }
  }
}
